"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useEffect, Suspense } from "react";
import { Link } from "@/components/LocaleLink";
import { useSearchParams } from "next/navigation";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Check, Sparkles, Zap, Crown, ArrowRight, XCircle } from "lucide-react";
import { Logo } from "@/components/Logo";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useAuthStore } from "@/lib/store";
import { useTranslation } from "@/lib/hooks/useTranslation";
import { CompetitorComparison } from "@/components/landing/CompetitorComparison";
const API_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:8000";
function PricingContent() {
  const {
    accessToken,
    user
  } = useAuthStore();
  const {
    locale
  } = useTranslation();
  const searchParams = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [showError, setShowError] = useState(false);
  const hasPaidPlan = user?.plan === "premium" || user?.plan === "vip" || user?.plan === "school";
  useEffect(() => {
    if (searchParams.get("checkout") === "cancelled") {
      setShowError(true);
      // Remove the query param while keeping the current path — otherwise
      // users on /fr/pricing or /en/pricing would be bounced back to the
      // locale-less `/pricing` and lose their language context.
      if (typeof window !== "undefined") {
        window.history.replaceState({}, "", window.location.pathname);
      }
    }
  }, [searchParams]);
  const handleSubscribe = async () => {
    if (!accessToken) {
      const returnParam = searchParams.get("return");
      const redirectUrl = returnParam ? `/pricing?return=${returnParam}` : "/pricing";
      window.location.href = `/login?redirect_to=${encodeURIComponent(redirectUrl)}`;
      return;
    }
    setIsLoading(true);
    try {
      // Save return destination for after payment
      const returnParam_0 = searchParams.get("return");
      if (returnParam_0) {
        localStorage.setItem("premium_return_to", returnParam_0);
      }
      const res = await fetch(`${API_URL}/api/v1/stripe/create-checkout-session`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });
      if (!res.ok) throw new Error("Failed to create checkout session");
      const data = await res.json();
      window.location.href = data.checkout_url;
    } catch (error) {
      console.error("Checkout error:", error);
      setShowError(true);
    } finally {
      setIsLoading(false);
    }
  };
  const plans = [{
    name: "Free",
    price: locale === "fr" ? "0€" : "€0",
    period: locale === "fr" ? "/mois" : "/month",
    description: locale === "fr" ? "Pour découvrir Velyq" : "To discover Velyq",
    features: locale === "fr" ? ["3 entretiens par mois", "Questions personnalisées", "Feedback IA basique", "1 CV uploadé"] : ["3 interviews per month", "Personalized questions", "Basic AI feedback", "1 CV uploaded"],
    cta: user?.plan === "free" ? locale === "fr" ? "Plan actuel" : "Current plan" : accessToken ? locale === "fr" ? "Aller au tableau de bord" : "Go to dashboard" : locale === "fr" ? "Commencer gratuitement" : "Start for free",
    href: accessToken ? "/dashboard" : "/register",
    disabled: user?.plan === "free",
    highlighted: false,
    icon: Zap
  }, {
    name: "Premium",
    price: locale === "fr" ? "9,99€" : "€9.99",
    period: locale === "fr" ? "/mois" : "/month",
    description: locale === "fr" ? "Usage illimité, tout inclus" : "Unlimited, everything included",
    features: locale === "fr" ? ["Entretiens illimités (classiques + Live vocaux)", "Analyses ATS illimitées", "Lettres de motivation illimitées", "LinkedIn : rewrite IA", "CV Builder complet · Export PDF", "Modes tech, consulting, finance"] : ["Unlimited interviews (classic + voice Live)", "Unlimited ATS analyses", "Unlimited cover letters", "LinkedIn: AI rewrite", "Full Resume Builder · PDF export", "Tech, consulting, finance modes"],
    cta: locale === "fr" ? "Passer Premium" : "Go Premium",
    onClick: handleSubscribe,
    highlighted: true,
    icon: Crown
  }];
  return <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
      {/* Header */}
      <header className="border-b bg-background/95 backdrop-blur">
        <div className="container mx-auto flex h-16 items-center justify-between px-4">
          <Link href="/">
            <Logo />
          </Link>
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            {accessToken ? <Link href="/dashboard">
                <Button variant="outline">
                  {locale === "fr" ? "Dashboard" : "Dashboard"}
                </Button>
              </Link> : <>
                <Link href="/login">
                  <Button variant="ghost">
                    {locale === "fr" ? "Connexion" : "Login"}
                  </Button>
                </Link>
                <Link href="/register">
                  <Button>
                    {locale === "fr" ? "S'inscrire" : "Sign up"}
                  </Button>
                </Link>
              </>}
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="container mx-auto px-4 py-16">
        {/* Hero */}
        <div className="mx-auto max-w-3xl text-center">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-1.5 text-sm font-medium text-primary">
            <Sparkles className="h-4 w-4" />
            {locale === "fr" ? "Tarifs simples" : "Simple pricing"}
          </div>
          <h1 className="mb-4 text-4xl font-bold tracking-tight sm:text-5xl">
            {locale === "fr" ? "Choisissez votre plan" : "Choose your plan"}
          </h1>
          <p className="text-lg text-muted-foreground">
            {locale === "fr" ? "Commencez gratuitement, passez Premium quand vous êtes prêt." : "Start for free, upgrade to Premium when you're ready."}
          </p>
        </div>

        {/* Error Banner */}
        {showError && <div className="mx-auto mt-8 max-w-md">
            <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900 dark:bg-red-950/50">
              <XCircle className="h-5 w-5 shrink-0 text-red-600 dark:text-red-400" />
              <div>
                <p className="font-medium text-red-800 dark:text-red-200">
                  {locale === "fr" ? "Paiement annulé" : "Payment cancelled"}
                </p>
                <p className="text-sm text-red-600 dark:text-red-400">
                  {locale === "fr" ? "Le paiement n'a pas abouti. Vous pouvez réessayer quand vous voulez." : "The payment was not completed. You can try again anytime."}
                </p>
              </div>
              <button onClick={() => setShowError(false)} className="ml-auto text-red-600 hover:text-red-800 dark:text-red-400">
                ✕
              </button>
            </div>
          </div>}

        {/* Plans */}
        <div className="mx-auto mt-16 grid max-w-3xl gap-6 md:grid-cols-2">
          {plans.map(plan => <Card key={plan.name} className={`relative flex flex-col ${plan.highlighted ? "border-primary shadow-lg shadow-primary/10" : ""}`}>
              {plan.highlighted && <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="rounded-full bg-primary px-3 py-1 text-xs font-medium text-primary-foreground">
                    {locale === "fr" ? "Populaire" : "Popular"}
                  </span>
                </div>}
              <CardHeader>
                <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                  <plan.icon className="h-5 w-5 text-primary" />
                </div>
                <CardTitle className="text-2xl">{plan.name}</CardTitle>
                <CardDescription>{plan.description}</CardDescription>
              </CardHeader>
              <CardContent className="flex-1">
                <div className="mb-6">
                  <span className="text-4xl font-bold">{plan.price}</span>
                  <span className="text-muted-foreground">{plan.period}</span>
                </div>
                <ul className="space-y-3">
                  {plan.features.map(feature => <li key={feature} className="flex items-start gap-2">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                      <span className="text-sm">{feature}</span>
                    </li>)}
                </ul>
              </CardContent>
              <CardFooter>
                {plan.onClick ? <Button className="w-full gap-2" size="lg" onClick={plan.onClick} disabled={isLoading || hasPaidPlan}>
                    {hasPaidPlan ? locale === "fr" ? "Déjà Premium ✓" : "Already Premium ✓" : <>
                        {plan.cta}
                        <ArrowRight className="h-4 w-4" />
                      </>}
                  </Button> : plan.disabled ? <Button variant="outline" className="w-full" size="lg" disabled>
                    {plan.cta} ✓
                  </Button> : <Link href={plan.href!} className="w-full">
                    <Button variant="outline" className="w-full" size="lg">
                      {plan.cta}
                    </Button>
                  </Link>}
              </CardFooter>
            </Card>)}
        </div>

        {/* Competitor comparison — AEO extractable, positions Velyq explicitly */}
        <div className="mt-20">
          <CompetitorComparison locale={locale} />
        </div>

        {/* FAQ */}
        <div className="mx-auto mt-24 max-w-2xl">
          <h2 className="mb-8 text-center text-2xl font-bold">
            {locale === "fr" ? "Questions fréquentes" : "FAQ"}
          </h2>
          <div className="space-y-6">
            <div>
              <h3 className="mb-2 font-semibold">
                {locale === "fr" ? "Puis-je annuler à tout moment ?" : "Can I cancel anytime?"}
              </h3>
              <p className="text-muted-foreground">
                {locale === "fr" ? "Oui, vous pouvez annuler votre abonnement à tout moment depuis votre espace client. Vous gardez l'accès jusqu'à la fin de la période payée." : "Yes, you can cancel your subscription anytime from your account. You keep access until the end of the paid period."}
              </p>
            </div>
            <div>
              <h3 className="mb-2 font-semibold">
                {locale === "fr" ? "Le plan Free est-il vraiment gratuit ?" : "Is the Free plan really free?"}
              </h3>
              <p className="text-muted-foreground">
                {locale === "fr" ? "Oui, 100% gratuit, sans carte bancaire requise. Vous pouvez utiliser Velyq avec les limites du plan Free aussi longtemps que vous le souhaitez." : "Yes, 100% free, no credit card required. You can use Velyq with Free plan limits as long as you want."}
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>;
}
export default function PricingPage() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Suspense fallback={<div className="flex min-h-screen items-center justify-center">Chargement...</div>}><PricingContent /></Suspense>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}